import { Badge, Box, Button, Group, MultiSelect } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../modal/modal';

import classes from './clause-campaigns-selector.module.css';

import { clauseCampaignsAtom, useClauseCampaigns } from '@data-access';

export function ClauseCampaignsSelector() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [opened, actions] = useDisclosure();
  const [clauseCampaigns, setClauseCampaigns] = useAtom(clauseCampaignsAtom);
  const [pendingValues, setPendingValues] = useState<string[]>(clauseCampaigns);
  const { data: options = [], isLoading } = useClauseCampaigns();

  useEffect(() => {
    if (opened) {
      setPendingValues(clauseCampaigns);
    }
  }, [opened, clauseCampaigns]);

  const handleConfirm = () => {
    setClauseCampaigns(pendingValues);
    actions.close();
    queryClient.invalidateQueries();
  };

  const handleReset = () => {
    setClauseCampaigns([]);
    setPendingValues([]);
    actions.close();
    queryClient.invalidateQueries();
  };

  return (
    <>
      <Button
        color="pf-green"
        onClick={actions.open}
        rightSection={
          <Badge
            color={clauseCampaigns.length > 0 ? 'orange' : 'pf-green'}
            variant="light"
          >
            {clauseCampaigns.length}
          </Badge>
        }
        variant="white"
      >
        {t('common.x_clause')}
      </Button>
      <Modal
        classNames={{
          root: clsx(classes.modal),
          content: clsx(classes.modalContent),
          header: clsx(classes.modalHeader),
          title: clsx(classes.modalTitle),
          body: clsx(classes.modalBody),
        }}
        onClose={actions.close}
        opened={opened}
        size="sm"
        title={t('common.search_x_clause')}
      >
        <Box p="md" w="100%">
          <MultiSelect
            data={options}
            value={pendingValues}
            onChange={setPendingValues}
            placeholder={t('common.search_x_clause')}
            searchable
            clearable
            disabled={isLoading}
          />
          <Group mt="md">
            <Button onClick={handleConfirm}>{t('common.confirm')}</Button>
            <Button onClick={actions.close} variant="subtle">
              {t('common.cancel')}
            </Button>
            <Button color="red" onClick={handleReset} variant="subtle">
              {t('common.reset')}
            </Button>
          </Group>
        </Box>
      </Modal>
    </>
  );
}

export default ClauseCampaignsSelector;
